import type { ListDiff } from './diffOrderedLists';
import { diffOrderedUniqueLists } from './diffOrderedUniqueLists';

export type OrderedSetDiff = [
    number | null,
    Array<{ move: number; insert?: undefined } | { insert: string; move?: undefined }>,
];

/**
 * Similar to diffOrderedLists, but for ordered sets of unique values.
 */
export function diffOrderedSets(
    before: readonly string[],
    after: readonly string[]
): OrderedSetDiff[] {
    const diffRes = diffOrderedUniqueLists(before, after);
    const result: OrderedSetDiff[] = [];

    const getNewIndex = (index: number | null) => {
        let array = result.find((each) => each[0] === index);
        if (!array) {
            array = [index, []];
            result.push(array);
        }
        return array[1];
    };

    const addMove = (newIndex: number | null, oldIndex: number) => {
        const entries = getNewIndex(newIndex);
        entries.push({ move: oldIndex });
    };

    const addInsert = (newIndex: number, value: string) => {
        const entries = getNewIndex(newIndex);
        entries.push({ insert: value });
    };

    const addDelete = (oldIndex: number) => {
        addMove(null, oldIndex);
    };

    const cutValues = new Map<string, number>();

    const { inserts, deletes } = compressListDiffs(diffRes);

    // find all deletes who are actually moved/cut values:
    deletes.forEach((each) => {
        const index = each.index;
        if (before[index] !== undefined && after.includes(before[index])) {
            cutValues.set(before[index], index);
        } else {
            addDelete(index);
        }
    });

    // find out which inserts are actually moved values:
    inserts.forEach((each) => {
        each.values.forEach((value) => {
            const currentCutIndex = cutValues.get(value);
            if (currentCutIndex === undefined) {
                addInsert(each.index, value);
            } else {
                addMove(each.index, currentCutIndex);
            }
        });
    });

    return result;
}

function compressListDiffs(diff: Array<ListDiff<string>>) {
    const inserts: Array<{ index: number; values: string[] }> = [];
    const deletes: Array<{ index: number }> = [];
    let originIndex = 0;
    diff.forEach((each) => {
        switch (each.op) {
            case '=':
                originIndex += each.values.length;
                break;
            case '+':
                inserts.push({ index: originIndex - 1, values: each.values });
                break;
            case '-':
                each.values.forEach((_, i) => {
                    deletes.push({ index: originIndex + i });
                });
                originIndex += each.values.length;
        }
    });

    return { inserts, deletes };
}
